import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { usePartnerProducts } from '@/hooks/usePartnerProducts';
import { Badge, Button, Card, CardTitle } from '@/components/ui';
import { categoryTheme } from '@/lib/categoryTheme';
import { toast } from '@/components/ui/Toast';
import { useCart } from '@/store/cart';
import { formatCOP } from '@/lib/format';

/**
 * StoreProduct — ROUND7 P1 / ROUND8. Detalle de un producto partner +
 * stepper qty + "Agregar al carrito" + "Comprar ahora". Datos reales desde
 * `public.partner_products` vía `usePartnerProducts()`.
 */
export function StoreProductPage(): JSX.Element {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [qty, setQty] = useState(1);
  const { data: products, isLoading, isError } = usePartnerProducts();
  const addProductLine = useCart((s) => s.addProductLine);

  if (isLoading) {
    return (
      <section className="container-fc py-12" aria-busy="true">
        <div className="h-8 w-56 rounded bg-neutral-100" />
        <div className="mt-4 h-5 w-full max-w-md rounded bg-neutral-100" />
        <div className="mt-8 h-40 w-full rounded-card bg-neutral-100" />
      </section>
    );
  }

  const product = products?.find((p) => p.id === productId) ?? null;

  if (isError || !product) {
    return (
      <section className="container-fc py-12">
        <h1 className="font-display text-3xl">
          {isError ? 'No pudimos cargar el producto' : 'Producto no encontrado'}
        </h1>
        <p className="mt-2 text-fc-muted">
          {isError
            ? 'Ocurrió un error al cargar este producto. Probá recargar la página en unos minutos.'
            : 'El producto que buscás no está disponible. Volvé a la tienda.'}
        </p>
        <Button className="mt-6" variant="secondary" onClick={() => navigate('/store')}>
          Volver a la tienda
        </Button>
      </section>
    );
  }

  const maxQty = Math.max(1, Math.min(product.stock || 1, 10));
  const lineTotal = product.priceCents * qty;

  function handleAdd(): void {
    if (!product) return;
    addProductLine({
      productId: product.id,
      partnerId: product.partnerId,
      partnerName: product.partnerName,
      title: product.title,
      unitPriceCents: product.priceCents,
      quantity: qty,
    });
    toast.success({
      title: 'Agregado al carrito',
      description: `${qty}× ${product.title}`,
    });
  }

  function handleBuyNow(): void {
    handleAdd();
    navigate('/cart');
  }

  return (
    <section className="container-fc py-8 md:py-12">
      <nav aria-label="Breadcrumb" className="mb-4 text-sm text-fc-muted">
        <Link to="/store" className="hover:text-fc-ink">
          Tienda
        </Link>
        <span aria-hidden> / </span>
        <Link to={`/store/category/${product.category}`} className="hover:text-fc-ink">
          {product.categoryLabel}
        </Link>
      </nav>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
        <div
          role="img"
          aria-label="Imagen del producto (placeholder)"
          className="flex aspect-square items-center justify-center rounded-card border border-neutral-200 bg-neutral-50 text-neutral-400"
        >
          <span aria-hidden className="text-6xl">
            🎁
          </span>
        </div>
        <div>
          <Badge tone={categoryTheme(product.category).tone} className="mb-3">
            {product.categoryLabel}
          </Badge>
          <h1 className="font-display text-3xl md:text-4xl">{product.title}</h1>
          <p className="mt-2 text-sm text-fc-muted">por {product.partnerName}</p>

          {product.description && (
            <p className="mt-6 text-base text-fc-ink">{product.description}</p>
          )}

          <div className="mt-8 flex items-end justify-between gap-4 border-t border-neutral-200 pt-6">
            <div>
              <div className="text-xs uppercase tracking-wide text-fc-muted">Precio</div>
              <div className="text-3xl font-semibold tabular-nums">
                {formatCOP(product.priceCents)}
              </div>
            </div>
            <div className="text-right text-sm text-fc-muted">
              Stock disponible: <span className="font-medium text-fc-ink">{product.stock}</span>
            </div>
          </div>

          <Card variant="ghost" className="mt-6" padded>
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-fc-muted">Cantidad</span>
              <div className="inline-flex items-center rounded-card border border-neutral-200">
                <button
                  type="button"
                  aria-label="Disminuir"
                  className="h-9 w-9 hover:bg-neutral-50 disabled:opacity-40"
                  disabled={qty <= 1}
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                >
                  −
                </button>
                <span aria-live="polite" className="min-w-[2.5ch] px-3 text-center tabular-nums">
                  {qty}
                </span>
                <button
                  type="button"
                  aria-label="Aumentar"
                  className="h-9 w-9 hover:bg-neutral-50 disabled:opacity-40"
                  disabled={qty >= maxQty}
                  onClick={() => setQty((q) => Math.min(maxQty, q + 1))}
                >
                  +
                </button>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between gap-3">
              <span className="text-sm text-fc-muted">Subtotal</span>
              <CardTitle>{formatCOP(lineTotal)}</CardTitle>
            </div>
          </Card>

          <div className="mt-6 flex flex-col gap-2 md:flex-row">
            <Button variant="secondary" size="lg" onClick={handleAdd} disabled={product.stock <= 0}>
              Agregar al carrito
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={handleBuyNow}
              disabled={product.stock <= 0}
            >
              Comprar ahora
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
